import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches a page that fails while drawing, so one broken screen shows a
 * message in its own space instead of blanking the whole app. The sidebar and
 * header sit outside it and keep working. Layout remounts it (by `key`) on
 * every navigation, so moving to another page clears the error.
 */
export default class PageErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('A page failed to draw:', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <div role="alert" className="mx-auto mt-10 max-w-xl rounded-lg border border-amber-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="space-y-2">
            <h2 className="font-semibold text-gray-900">This part of the page could not be shown</h2>
            <p className="text-sm text-gray-600">
              Something in it failed to load properly. Nothing you saved is affected. Try again, or use the menu to go to another page.
            </p>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">Details for support</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-gray-50 p-2">{error.message}</pre>
            </details>
            <button type="button" onClick={() => this.setState({ error: null })} className="btn btn-outline mt-1">
              <RotateCcw className="mr-2 h-4 w-4" />Try again
            </button>
          </div>
        </div>
      </div>
    )
  }
}
